<ui-section>
  @if (schedulingChoices().length > 1) {
    <label for="hearing-request-choice">Hearing
      <select id="hearing-request-choice" (change)="chooseSchedulingHearing($any($event.target).value)">
        <option value="">Choose a hearing</option>
        @for (choice of schedulingChoices(); track choice.id) { <option [value]="choice.id">{{choice.label}}</option> }
      </select>
    </label>
  }
  @if (workflowReturnRoute(); as returnRoute) {
    <a [routerLink]="returnRoute">Return to workflow step</a>
  }
  <ui-block>
    <!-- All toolbar controls live in the panel header band so they sit in the top "Scheduling" bar,
         flush with the calendar width. Individual ui-buttons with a direct [severity] binding so the
         active highlight repaints reactively (the ez-button-group reads severity from config at init). -->
    <ng-template #scheduleHeader>
      <div class="schedule-header">
        <!-- The period nav (prev / clickable label / next) replaces the page title. Clicking the label
             opens a month/year picker to jump anywhere. Calendar mode only (the List view has no period). -->
        <div *ngIf="viewMode() === 'calendar'" class="schedule-header__period">
          <button
            type="button"
            class="month-nav"
            data-testid="period-prev"
            aria-label="Previous period"
            (click)="onPrevPeriod()"
          >
            &#8249;
          </button>
          <button
            type="button"
            class="schedule-header__period-label"
            data-testid="period-label"
            [attr.aria-expanded]="showMonthPicker()"
            (click)="toggleMonthPicker()"
          >
            {{ periodLabel() }}
          </button>
          <button
            type="button"
            class="month-nav"
            data-testid="period-next"
            aria-label="Next period"
            (click)="onNextPeriod()"
          >
            &#8250;
          </button>

          <div
            *ngIf="showMonthPicker()"
            class="month-picker"
            data-testid="month-picker"
          >
            <div class="month-picker__year">
              <button
                type="button"
                class="month-nav"
                aria-label="Previous year"
                (click)="pickerPrevYear()"
              >
                &#8249;
              </button>
              <span
                class="month-picker__year-label"
                data-testid="picker-year"
                >{{ pickerYear() }}</span
              >
              <button
                type="button"
                class="month-nav"
                aria-label="Next year"
                (click)="pickerNextYear()"
              >
                &#8250;
              </button>
            </div>
            <div class="month-picker__grid">
              <button
                *ngFor="let month of pickerMonths; let i = index"
                type="button"
                class="month-picker__month"
                data-testid="picker-month"
                (click)="selectPickerMonth(i)"
              >
                {{ month.slice(0, 3) }}
              </button>
            </div>
          </div>
        </div>

        <div
          *ngIf="viewMode() === 'calendar'"
          class="schedule-toolbar__views"
          data-testid="calendar-view-switch"
        >
          <ui-button
            buttonText="Day"
            [severity]="
              calendarView() === CalendarView.Day ? 'primary' : 'secondary'
            "
            (click)="setCalendarView(CalendarView.Day)"
          ></ui-button>
          <ui-button
            buttonText="Week"
            [severity]="
              calendarView() === CalendarView.Week ? 'primary' : 'secondary'
            "
            (click)="setCalendarView(CalendarView.Week)"
          ></ui-button>
          <ui-button
            buttonText="Month"
            [severity]="
              calendarView() === CalendarView.Month ? 'primary' : 'secondary'
            "
            (click)="setCalendarView(CalendarView.Month)"
          ></ui-button>
        </div>

        <!-- Facility filter (#1458): narrows BOTH the calendar events and the eligible strip to
             one location's schedule. Options are the distinct locations present in the loaded
             hearings + eligible backlog; "All facilities" restores everything. -->
        <div class="schedule-toolbar__facility">
          <label
            class="schedule-toolbar__facility-label"
            for="facility-filter"
            >{{ facilityText.label() }}</label
          >
          <select
            id="facility-filter"
            class="facility-filter"
            data-testid="facility-filter"
            [ngModel]="facilityFilter()"
            (ngModelChange)="setFacilityFilter($event)"
          >
            <option value="all">{{ facilityText.all() }}</option>
            <option
              *ngFor="let facility of facilityOptions()"
              [value]="facility"
            >
              {{ facility }}
            </option>
          </select>
        </div>

        <!-- Commissioner filter (#1531): sits directly beside the Facility dropdown so the two read
             as a pair. Narrows the calendar to one official's hearings through the same
             visibleHearings choke point the facility filter uses, so every view narrows together.
             The option VALUE is the official's user id, not the display name — two officials can
             share a name, and keying on the name would merge their calendars. Both labels resolve
             through terminology, so a jurisdiction can rename them in Admin > Terminology. -->
        <div class="schedule-toolbar__official">
          <label
            class="schedule-toolbar__official-label"
            for="official-filter"
            >{{ hearingText.official() }}</label
          >
          <select
            id="official-filter"
            class="official-filter"
            data-testid="official-filter"
            [ngModel]="officialFilter()"
            (ngModelChange)="setOfficialFilter($event)"
          >
            <option value="all">{{ hearingText.allOfficials() }}</option>
            <option
              *ngFor="let official of officialOptions()"
              [value]="official.userId"
            >
              {{ official.displayName }}
            </option>
          </select>
        </div>

        <div class="schedule-toolbar__right" data-testid="view-controls">
          <!-- Manual refresh (#1441, formerly the vague "Update Now"). Re-queries the calendar
               projection so changes made by other users appear without leaving the page; the label
               goes busy and the button disables while the fetch runs, and the "Updated h:mm" note
               stamps each successful fetch. -->
          <span
            *ngIf="lastUpdatedLabel() as updatedAt"
            class="schedule-toolbar__updated"
            data-testid="last-updated"
            >Updated {{ updatedAt }}</span
          >
          <ui-button
            data-testid="refresh-schedule"
            [buttonText]="
              isLoading() ? 'Refreshing&#8230;' : 'Refresh Schedule'
            "
            severity="secondary"
            [tooltip]="hearingText.reloadHint()"
            [disabled]="isLoading()"
            (click)="load()"
          ></ui-button>
          <ui-button
            buttonText="List"
            [severity]="viewMode() === 'grid' ? 'primary' : 'secondary'"
            (click)="setView('grid')"
          ></ui-button>
          <ui-button
            buttonText="Calendar"
            [severity]="viewMode() === 'calendar' ? 'primary' : 'secondary'"
            (click)="setView('calendar')"
          ></ui-button>
        </div>
      </div>
    </ng-template>

    <ui-panel
      width="fill"
      class="hearing-schedule"
      [headerTemplate]="scheduleHeader"
    >
      <!-- Dashboard->scheduling deep-link explanation (#1442 AC4): shown when the chosen inmate
           cannot enter the Add-to-Calendar flow (not ready / already scheduled / no permission).
           Dismissible; the rest of the page stays fully usable underneath. -->
      <div
        *ngIf="scheduleNotice() as notice"
        class="schedule-notice"
        role="status"
        data-testid="schedule-preselect-notice"
      >
        <span class="schedule-notice__text">{{ notice }}</span>
        <button
          type="button"
          class="schedule-notice__dismiss"
          aria-label="Dismiss notice"
          data-testid="schedule-notice-dismiss"
          (click)="dismissScheduleNotice()"
        >
          &times;
        </button>
      </div>

      <!-- First-available banner (#1487). The calendar jumps to the week holding the earliest day
           this hearing can actually be placed, so say which day that is and why the view moved —
           otherwise the jump looks arbitrary. Rendered in every view, since the week and day grids
           come from angular-calendar and cannot mark a cell the way the month grid can. -->
      <div
        *ngIf="firstAvailable() as marked"
        class="first-available-banner"
        role="status"
        data-testid="first-available-banner"
      >
        <span class="first-available-banner__text">
          First available date for {{ marked.name }}:
          <strong data-testid="first-available-date">{{
            formatFirstAvailable(marked.date)
          }}</strong>
          — the calendar opened on that week.
        </span>
        <button
          type="button"
          class="first-available-banner__dismiss"
          aria-label="Dismiss first available date"
          data-testid="first-available-dismiss"
          (click)="dismissFirstAvailable()"
        >
          &times;
        </button>
      </div>

      <!-- Eligible-inmates strip: the ready-to-schedule backlog (same projection the search tray
           uses) rendered as a dashboard-style field grid above the calendar. Hidden from parole
           officials without scheduling authority; for other viewers the drag-to-a-day
           scheduling affordance appears only when the shared scheduling gate
           (scheduling-access.ts, #1442) is on. -->
      <div
        *ngIf="!hidesAwaitingScheduling()"
        class="eligible-strip"
        data-testid="eligible-strip"
      >
        <div class="eligible-strip__header">
          <span class="eligible-strip__title">Awaiting scheduling</span>
          <span
            class="eligible-strip__count"
            data-testid="eligible-strip-count"
            >{{ eligibleStripCandidates().length }}</span
          >
          <span
            *ngIf="canMutateHearings() && eligibleStripCandidates().length > 0"
            class="eligible-strip__hint"
            data-testid="eligible-strip-hint"
            >{{ subjectText.dragHint() }}</span
          >
        </div>

        <p
          *ngIf="!eligibleBacklogLoaded()"
          class="eligible-strip__empty"
          data-testid="eligible-strip-loading"
        >
          {{ subjectText.eligibleLoading() }}
        </p>

        <!-- Diagnosable backlog failure: a ready-to-schedule fetch error renders with its HTTP
             status + server problem title instead of silently posing as an empty backlog. -->
        <p
          *ngIf="
            eligibleBacklogLoaded() && eligibleBacklogError() as backlogError
          "
          class="eligible-strip__error"
          role="alert"
          data-testid="eligible-strip-error"
        >
          {{ backlogError }}
        </p>

        <p
          *ngIf="
            eligibleBacklogLoaded() &&
            !eligibleBacklogError() &&
            eligibleStripCandidates().length === 0
          "
          class="eligible-strip__empty"
          data-testid="eligible-strip-empty"
        >
          {{ subjectText.awaitingEmpty() }}
        </p>

        <div
          *ngIf="
            eligibleBacklogLoaded() && eligibleStripCandidates().length > 0
          "
          class="eligible-grid__head"
          data-testid="eligible-grid-head"
        >
          <span>Last Name</span>
          <span>First Name</span>
          <span>{{ subjectText.identifierHeader() }}</span>
          <span>Due</span>
          <span>Location</span>
        </div>

        <div
          *ngIf="
            eligibleBacklogLoaded() && eligibleStripCandidates().length > 0
          "
          class="eligible-strip__list"
          data-testid="eligible-strip-list"
          cdkDropList
          [cdkDropListSortingDisabled]="true"
          (cdkDropListDropped)="onEligibleDropped($event)"
        >
          <div
            *ngFor="
              let candidate of eligibleStripCandidates();
              trackBy: trackByEligible
            "
            class="eligible-card"
            data-testid="eligible-strip-card"
            cdkDrag
            [cdkDragData]="candidate"
            [cdkDragDisabled]="!canMutateHearings()"
            [class.eligible-card--draggable]="canMutateHearings()"
            [attr.title]="
              canMutateHearings()
                ? 'Drag onto a calendar day to schedule'
                : null
            "
            (cdkDragStarted)="onEligibleDragStarted()"
            (cdkDragEnded)="onEligibleDragEnded()"
          >
            <span class="eligible-card__name">{{
              personLastName(candidate.title)
            }}</span>
            <span class="eligible-card__name">{{
              personFirstNames(candidate.title)
            }}</span>
            <span class="eligible-card__cell">{{
              candidate.inmateNumber?.trim() || "—"
            }}</span>
            <span class="eligible-card__cell">{{
              eligibleDateLabel(candidate)
            }}</span>
            <span class="eligible-card__cell">{{
              eligibleLocationLabel(candidate)
            }}</span>
          </div>
        </div>
      </div>

      <div
        *ngIf="viewMode() === 'grid'"
        class="schedule-sort"
        data-testid="sort-controls"
      >
        <span class="schedule-sort__label">Sort by</span>
        <ui-button-group
          [buttonsConfig]="sortButtons()"
          (buttonClick)="onSortButtonClick($event)"
        ></ui-button-group>
      </div>

      <div
        *ngIf="isLoading()"
        class="state loading"
        data-testid="loading-state"
      >
        {{ hearingText.loading() }}
      </div>

      <div
        *ngIf="!isLoading() && errorState() as error"
        class="state error"
        data-testid="error-state"
      >
        <strong>{{ error.title }}</strong>
        <!-- A refused write carries issue codes (#1526): show each code beside its detail instead
             of the plain message list, so the specific rule is never lost or repeated. -->
        <ng-container *ngIf="error.issues.length > 0; else plainMessages">
          <p
            *ngFor="let issue of error.issues"
            class="message"
            data-testid="error-issue"
          >
            <code class="issue-code">{{ issue.code }}</code> {{ issue.message }}
          </p>
        </ng-container>
        <ng-template #plainMessages>
          <p *ngFor="let message of error.messages" class="message">
            {{ message }}
          </p>
        </ng-template>
      </div>

      <!-- List mode only: the calendar views render even with zero hearings so eligible inmates
           can always be dragged onto a day (an empty week is exactly when scheduling happens). -->
      <div
        *ngIf="
          !isLoading() &&
          !errorState() &&
          !hasHearings() &&
          viewMode() === 'grid'
        "
        class="state"
        data-testid="empty-state"
      >
        {{ hearingText.empty() }}
      </div>

      <ng-container *ngIf="!isLoading() && !errorState()">
        <ng-container *ngIf="viewMode() === 'calendar'">
          <div
            *ngIf="calendarView() === CalendarView.Month"
            class="calendar-host"
            data-testid="calendar-month-view"
            [class.calendar-host--drop-active]="eligibleDragActive()"
          >
            <mwl-calendar-month-view
              [viewDate]="viewDate()"
              [events]="events()"
              [cellTemplate]="monthCellTemplate"
              (eventClicked)="openHearing($any($event).event.meta)"
            ></mwl-calendar-month-view>

            <!-- The wrapper stamps the cell's date so a dropped eligible-strip card can resolve the
                 day under the pointer (resolveDropDate). -->
            <ng-template #monthCellTemplate let-day="day">
              <div
                class="month-cell-drop"
                [class.month-cell-drop--first-available]="
                  isFirstAvailableDay(day.date)
                "
                [attr.data-drop-date]="day.date.toISOString()"
                [attr.data-first-available]="
                  isFirstAvailableDay(day.date) ? 'true' : null
                "
                data-testid="month-cell-drop"
              >
                <div class="cal-cell-top">
                  <span class="cal-day-number">{{
                    day.date | calendarDate: "monthViewDayNumber" : "en"
                  }}</span>
                </div>
                <!-- Chip-drag reschedule (#1458): each chip is a cdkDrag carrying its proceeding.
                     The list is connected to nothing (mirroring the eligible strip), so a release
                     fires on this source list and onScheduledChipDropped resolves the day cell
                     under the pointer; the drag affordance is gated on canMutateHearings. -->
                <div
                  class="month-chips"
                  data-testid="month-day-chips"
                  cdkDropList
                  [cdkDropListSortingDisabled]="true"
                  (cdkDropListDropped)="onScheduledChipDropped($event)"
                >
                  <button
                    *ngFor="let event of day.events"
                    type="button"
                    class="month-chip"
                    data-testid="month-chip"
                    cdkDrag
                    [cdkDragData]="event.meta"
                    [cdkDragDisabled]="!canMutateHearings()"
                    [class.month-chip--draggable]="canMutateHearings()"
                    [attr.data-readiness]="readinessOf(event.meta)"
                    [class.month-chip--needs-input]="
                      needsHearingInput(event.meta)
                    "
                    [attr.data-needs-input]="
                      needsHearingInput(event.meta) ? 'true' : null
                    "
                    [attr.title]="
                      needsHearingInput(event.meta)
                        ? needsHearingInputHint
                        : null
                    "
                    (cdkDragStarted)="onEligibleDragStarted()"
                    (cdkDragEnded)="onEligibleDragEnded()"
                    (click)="openHearing(event.meta)"
                  >
                    <span
                      class="month-chip__dot"
                      [style.background]="event.color?.primary"
                    ></span>
                    <span class="month-chip__name">{{
                      formatPersonName(event.meta.title)
                    }}</span>
                    <span class="month-chip__time">{{
                      formatScheduledTime(event.meta.scheduledStartUtc)
                    }}</span>
                  </button>
                </div>
              </div>
            </ng-template>
          </div>

          <div
            *ngIf="calendarView() === CalendarView.Week"
            class="calendar-host"
            data-testid="calendar-week-view"
            [class.calendar-host--drop-active]="eligibleDragActive()"
          >
            <div class="month-header" data-testid="week-header">
              <div class="month-header__summary" data-testid="week-summary">
                {{ weekHearingCount() }} hearings / {{ weekReadyCount() }} Ready
              </div>
            </div>

            <!-- Drag-to-reschedule (#1458): events carry draggable (gated on canMutateHearings) so
                 angular-calendar's native time-grid drag emits eventTimesChanged on release. -->
            <mwl-calendar-week-view
              [viewDate]="viewDate()"
              [events]="events()"
              [dayStartHour]="gridDayStartHour()"
              [dayEndHour]="gridDayEndHour()"
              [hourSegmentHeight]="20"
              (eventClicked)="openHearing($any($event).event.meta)"
              (eventTimesChanged)="onCalendarEventTimesChanged($any($event))"
            ></mwl-calendar-week-view>
          </div>

          <div
            *ngIf="calendarView() === CalendarView.Day"
            class="calendar-host"
            data-testid="calendar-day-view"
            [class.calendar-host--drop-active]="eligibleDragActive()"
          >
            <div class="month-header" data-testid="day-header">
              <div class="month-header__summary" data-testid="day-summary">
                {{ dayHearingCount() }} hearings / {{ dayReadyCount() }} Ready
              </div>
            </div>

            <mwl-calendar-day-view
              [viewDate]="viewDate()"
              [events]="events()"
              [dayStartHour]="gridDayStartHour()"
              [dayEndHour]="gridDayEndHour()"
              [hourSegmentHeight]="20"
              (eventClicked)="openHearing($any($event).event.meta)"
              (eventTimesChanged)="onCalendarEventTimesChanged($any($event))"
            ></mwl-calendar-day-view>
          </div>
        </ng-container>

        <div
          *ngIf="viewMode() === 'grid'"
          class="list-view"
          data-testid="list-view"
        >
          <section *ngFor="let day of days()" class="calendar-day">
            <h3 class="calendar-day__title">{{ day.dateLabel }}</h3>
            <ul class="calendar-day__list">
              <li
                *ngFor="let hearing of day.hearings"
                class="calendar-card"
                role="button"
                tabindex="0"
                (click)="openHearing(hearing)"
                (keyup.enter)="openHearing(hearing)"
              >
                <div class="calendar-card__time">
                  {{ formatScheduledTime(hearing.scheduledStartUtc) }}
                </div>
                <div class="calendar-card__body">
                  <div class="calendar-card__title">
                    {{ formatPersonName(hearing.title) }}
                    <span class="calendar-card__id"
                      >({{ hearing.proceedingNumber }})</span
                    >
                  </div>
                  <div class="calendar-card__meta">
                    {{ hearing.locationName?.trim() || "No location" }} ·
                    {{ formatResource(hearing) }}
                  </div>
                </div>
                <span class="badge">{{
                  formatStatus(hearing.schedulingStatusCode)
                }}</span>
                <!-- Overdue past-date state (#1447): the chip is the CTA — it deep-links to the
                     at-hearing input capture surface for this hearing (AC3). -->
                <button
                  *ngIf="needsHearingInput(hearing)"
                  type="button"
                  class="badge badge--needs-input"
                  data-testid="needs-input-chip"
                  [attr.title]="needsHearingInputHint"
                  (click)="
                    $event.stopPropagation(); recordHearingInput(hearing)
                  "
                >
                  {{ needsHearingInputLabel }}
                </button>
                <ui-button
                  *ngIf="canMutateHearings() && hearing.isScheduled"
                  class="calendar-card__reschedule"
                  data-testid="reschedule-hearing"
                  buttonText="Reschedule"
                  severity="secondary"
                  [tooltip]="hearingText.changeScheduleHint()"
                  [disabled]="mutatingProceedingId() !== null"
                  (click)="
                    $any($event).event?.stopPropagation();
                    rescheduleHearing(hearing)
                  "
                ></ui-button>
                <ui-button
                  *ngIf="canMutateHearings()"
                  class="calendar-card__remove"
                  data-testid="remove-hearing"
                  [buttonText]="hearingText.unschedule()"
                  severity="secondary"
                  [disabled]="mutatingProceedingId() !== null"
                  (click)="
                    $any($event).event?.stopPropagation();
                    confirmUnschedule(hearing)
                  "
                ></ui-button>
              </li>
            </ul>
          </section>
        </div>
      </ng-container>
    </ui-panel>
  </ui-block>
</ui-section>

<!-- Add-to-Calendar date picker. Opened from the top-nav search tray's "Add to Calendar" action; the
     coordinator clicks a day to schedule the chosen inmate's hearing on it. -->
<div
  *ngIf="pendingAddCandidate() as candidate"
  class="add-date-backdrop"
  data-testid="add-date-backdrop"
  (click)="cancelAddDate()"
>
  <div
    class="add-date-popover"
    data-testid="add-date-popover"
    (click)="$any($event).stopPropagation()"
  >
    <div class="add-date-popover__header">
      <span class="add-date-popover__title">Add to Calendar</span>
      <button
        type="button"
        class="add-date-popover__close"
        aria-label="Close"
        data-testid="add-date-close"
        (click)="cancelAddDate()"
      >
        &times;
      </button>
    </div>
    <p class="add-date-popover__name">
      {{ formatPersonName(candidate.title) }}
    </p>
    <p class="add-date-popover__hint">{{ hearingText.pickDay() }}</p>
    <p-datepicker
      [inline]="true"
      [defaultDate]="addDateDefault()"
      [minDate]="addDateDefault()"
      [maxDate]="addDateMaximum()"
      [disabledDays]="[0, 6]"
      (onSelect)="onAddDateSelected($any($event))"
      data-testid="add-date-picker"
    ></p-datepicker>
  </div>
</div>

<phms-hearing-detail-flyout
  *ngIf="flyoutProceedingId() as flyoutId"
  [schedulingRequestId]="schedulingRequestFor(flyoutId)"
  [scheduledProceedingId]="flyoutId"
  [canReschedule]="canMutateHearings()"
  [canAssignOfficial]="canAssignOfficials()"
  [returnContext]="flyoutReturnContext()"
  (closed)="closeFlyout()"
  (moveDate)="moveDateFromFlyout($event)"
  (removeFromCalendar)="removeFromFlyout($event)"
></phms-hearing-detail-flyout>
